import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Button, IconButton, Menu } from 'react-native-paper';

import { makeOrganizationEditFormStyles } from '@/features/profile-setting/components/organization-edit/organizationEditFormStyles';
import type { OrganizationEditFormValues } from '@/features/profile-setting/components/organization-edit/types';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { OutlinedButton } from '@/shared/components/OutlinedButton';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  isPending: boolean;
  onDelete: () => void;
  onPreview: () => void;
  onSave: () => void;
};

export const FormActions: FC<Props> = ({
  isPending,
  onDelete,
  onPreview,
  onSave
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeOrganizationEditFormStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit'
  });
  const { control } = useFormContext<OrganizationEditFormValues>();
  // Subscribe via useFormState (not useFormContext) so the button state
  // re-renders when validation results change.
  const { isSubmitted, isValid, isSubmitSuccessful } = useFormState();
  const facts = useWatch({ control, name: 'facts' });
  const [menuVisible, setMenuVisible] = useState(false);

  const hasIncompleteFact = facts.some(
    (entry) =>
      isEmptyOrNull(entry.name.trim()) || isEmptyOrNull(entry.value.trim())
  );

  const canNotSubmit =
    (isSubmitted && !isValid && !isSubmitSuccessful) ||
    isPending ||
    hasIncompleteFact;

  return (
    <View style={styles.actions}>
      <ExtendedMenu
        visible={menuVisible}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <IconButton
            icon="dots-horizontal"
            mode="outlined"
            size={20}
            onPress={() => setMenuVisible(true)}
            disabled={isPending}
            accessibilityHint={t('actions.more')}
            accessibilityLabel={t('actions.more')}
          />
        }
      >
        <Menu.Item
          onPress={() => {
            setMenuVisible(false);
            onDelete();
          }}
          title={t('actions.delete')}
          titleStyle={styles.deleteMenuItemTitle}
        />
      </ExtendedMenu>
      <OutlinedButton
        onPress={onPreview}
        disabled={isPending}
        style={styles.actionButton}
      >
        {t('actions.preview')}
      </OutlinedButton>
      <Button
        mode="contained"
        onPress={onSave}
        loading={isPending}
        disabled={canNotSubmit}
        style={styles.actionButton}
      >
        {t('actions.save')}
      </Button>
    </View>
  );
};
